import { ChangeEvent, KeyboardEvent, useState } from 'react';

import { ChevronDownIcon, ChevronUpIcon, HelpIcon } from '@braskai/icons';
import { Button, Typography } from '@braskai/ui-kit';

import { Box, Stack, useMediaQuery, useTheme } from '@mui/material';

import { BRAND_BLUE } from '@/widgets/pricing/pricingCards/lib/constants';
import { getFormattedPlanPrice } from '@/widgets/pricing/pricingCards/lib/utils/getFormattedPlanPrice';

import { FeatureTooltip, useProducts } from '@/entities/pricing';

import { MONTHS_IN_YEAR } from '@/shared/constants/common';
import { RASK_PRICING_CHAT_LINK } from '@/shared/constants/docLinks';
import { PaymentInterval, Plan, PlanCategory, Price, Products } from '@/shared/types/Billing';
import { Switch } from '@/shared/ui/Switch';

import { COLLAPSED_ROW_COUNT, COMPARISON_ROWS, ComparisonRow, DASH, EXTRA_MINUTES_ROW_KEY } from './constants';

type ExpandedPlansFeaturesProps = {
  yearlySelected: boolean;
  onBuyPlan: (priceId: Price['id'], plan: Plan) => void;
  onToggle: (yearlySelected: boolean) => void;
};

const FEATURE_COLUMN_WIDTH = 320;
const CATEGORIES: PlanCategory[] = [
  PlanCategory.Creator,
  PlanCategory.CreatorPro,
  PlanCategory.Business,
  PlanCategory.Enterprise,
];
const CATEGORY_NAMES: Record<PlanCategory, string> = {
  [PlanCategory.Business]: 'Business',
  [PlanCategory.Creator]: 'Creator',
  [PlanCategory.CreatorPro]: 'Creator Pro',
  [PlanCategory.Enterprise]: 'Enterprise',
  [PlanCategory.Starter]: 'Starter',
};

const getCategoryPlans = (products: Products | undefined, category: PlanCategory) =>
  products?.plan
    .filter((plan) => plan.category === category && !plan.is_archived)
    .sort((a, b) => (a.metadata.bundle_position || 0) - (b.metadata.bundle_position || 0)) ?? [];

// The default (base) plan a column represents — same selection used by the plan cards.
const getDefaultPlan = (products: Products | undefined, category: PlanCategory) => {
  const plans = getCategoryPlans(products, category);
  return plans.find((plan) => plan.metadata.bundle_value_default) ?? plans[0];
};

const onEnterOrSpace = (event: KeyboardEvent, action: () => void) => {
  if (event.key === 'Enter' || event.key === ' ') {
    event.preventDefault();
    action();
  }
};

export const ExpandedPlansFeatures = ({ yearlySelected, onBuyPlan, onToggle }: ExpandedPlansFeaturesProps) => {
  const theme = useTheme();
  const isDesktopLayout = useMediaQuery(theme.breakpoints.up('laptop'), { noSsr: true });
  const { products } = useProducts(true);
  const [showAll, setShowAll] = useState(false);
  const [selectedCategoryIndex, setSelectedCategoryIndex] = useState(1);

  const handleSwitch = (event: ChangeEvent<HTMLInputElement>) => onToggle(event.target.checked);

  // Effective per-month price label for a category header, from live API data.
  const getPriceLabel = (category: PlanCategory): string => {
    if (category === PlanCategory.Enterprise) return 'Custom';
    const defaultPlan = getDefaultPlan(products, category);
    const interval = yearlySelected ? PaymentInterval.Year : PaymentInterval.Month;
    const price = defaultPlan?.prices.find((item) => item.status === 'active' && item.recurring?.interval === interval);
    if (!price) return 'Custom';
    const perMonth = getFormattedPlanPrice({ paymentInterval: price.recurring?.interval, price: price.unit_amount });
    return `$${perMonth} / mo`;
  };

  // Included minutes / year for a category — same base bundle the card uses, shown annually (×12).
  const getMinutesValue = (category: PlanCategory): string => {
    if (category === PlanCategory.Enterprise) return 'Custom';
    const defaultPlan = getDefaultPlan(products, category);
    const monthlyMinutes = defaultPlan ? Number(defaultPlan.metadata.bundle_value) : Number.NaN;
    return Number.isFinite(monthlyMinutes) ? String(monthlyMinutes * MONTHS_IN_YEAR) : 'Custom';
  };

  const getRowValues = (row: ComparisonRow): string[] =>
    row.isMinutesRow ? CATEGORIES.map((category) => getMinutesValue(category)) : row.values;

  // The sticky mobile "Get started" checks out the currently-selected plan tab.
  const handleGetStarted = () => {
    const category = CATEGORIES[selectedCategoryIndex];
    if (category === PlanCategory.Enterprise) {
      window.open(RASK_PRICING_CHAT_LINK, '_blank', 'noopener,noreferrer');
      return;
    }
    const plan = getDefaultPlan(products, category);
    const interval = yearlySelected ? PaymentInterval.Year : PaymentInterval.Month;
    const price = plan?.prices.find((item) => item.status === 'active' && item.recurring?.interval === interval);
    if (plan && price) {
      onBuyPlan(price.id, plan);
    }
  };

  // "Extra minutes" only applies to annual plans → hide it when Monthly is selected.
  const activeRows = yearlySelected
    ? COMPARISON_ROWS
    : COMPARISON_ROWS.filter((row) => row.key !== EXTRA_MINUTES_ROW_KEY);
  const visibleRows = showAll ? activeRows : activeRows.slice(0, COLLAPSED_ROW_COUNT);

  const renderLabel = (row: ComparisonRow) => (
    <Stack alignItems="center" direction="row" gap={10}>
      <Typography color="grey.500" variant="body-2-md">
        {row.label}
      </Typography>
      {row.tooltip && (
        <FeatureTooltip tooltipProps={{ title: row.tooltip.title, description: row.tooltip.description }}>
          {/* Only the "?" icon is the tooltip trigger (icon-only tap target on mobile). */}
          <Box sx={{ color: 'grey.400', cursor: 'pointer', display: 'inline-flex', flexShrink: 0 }}>
            <HelpIcon height={20} width={20} />
          </Box>
        </FeatureTooltip>
      )}
    </Stack>
  );

  const renderValue = (value: string) => (
    <Typography color={value === DASH ? 'grey.400' : 'grey.900'} variant="body-2-md">
      {value}
    </Typography>
  );

  const toggleShowAll = () => setShowAll((prev) => !prev);

  const expandButton = (
    <Stack
      sx={{
        backgroundColor: 'common.white',
        borderTop: '1px solid',
        borderTopColor: 'grey.200',
        paddingY: 16,
      }}
      alignItems="center"
      justifyContent="center"
    >
      <Stack
        sx={{
          '&:focus-visible': { outline: `2px solid ${theme.palette.primary[600]}`, outlineOffset: 2 },
          border: '1px solid',
          borderColor: 'grey.300',
          borderRadius: 8,
          color: 'grey.700',
          cursor: 'pointer',
          paddingX: 18,
          paddingY: 11,
        }}
        alignItems="center"
        aria-expanded={showAll}
        data-testid="comparison-see-all-features-toggle"
        direction="row"
        gap={8}
        justifyContent="center"
        role="button"
        tabIndex={0}
        onClick={toggleShowAll}
        onKeyDown={(event) => onEnterOrSpace(event, toggleShowAll)}
      >
        <Typography color="grey.700" variant="body-1-md">
          {showAll ? 'Show less' : 'See all features'}
        </Typography>
        {showAll ? (
          <ChevronUpIcon height={20} width={20} />
        ) : (
          <ChevronDownIcon height={20} width={20} />
        )}
      </Stack>
    </Stack>
  );

  const heading = (
    <Stack alignItems="center" gap={8} paddingX={16}>
      <Typography
        color="grey.900"
        sx={{ textAlign: 'center' }}
        variant={isDesktopLayout ? 'title-1-md' : 'title-2-md'}
      >
        Compare what changes when you scale
      </Typography>
      <Typography color="grey.500" sx={{ textAlign: 'center' }} variant="body-1-md">
        Choose the plan that matches how often you publish.
      </Typography>
    </Stack>
  );

  if (!isDesktopLayout) {
    const isEnterpriseSelected = CATEGORIES[selectedCategoryIndex] === PlanCategory.Enterprise;

    return (
      <Stack gap={24} sx={{ backgroundColor: 'primary.50', paddingY: 40, width: '100%' }}>
        {heading}
        {/* No overflow on the wrapper so the header can stick to the scroll container's top. */}
        <Stack sx={{ marginX: 16 }}>
          <Stack
            sx={{
              backgroundColor: 'common.white',
              border: '1px solid',
              borderColor: 'grey.200',
              borderTopLeftRadius: 16,
              borderTopRightRadius: 16,
              position: 'sticky',
              top: 0,
              zIndex: 2,
            }}
            alignItems="center"
            gap={8}
            paddingBottom={16}
            paddingTop={24}
            paddingX={16}
          >
            <Stack alignItems="center" direction="row" gap={8}>
              <Typography color="grey.500" variant="body-2-md">
                Bill annually
              </Typography>
              <Switch checked={yearlySelected} onChange={handleSwitch} />
            </Stack>

            <Stack direction="row" gap={4} paddingBottom={16} paddingTop={8} sx={{ width: '100%' }}>
              {CATEGORIES.map((category, index) => {
                const isSelected = index === selectedCategoryIndex;
                const selectCategory = () => setSelectedCategoryIndex(index);
                return (
                  <Stack
                    key={category}
                    sx={{
                      '&:focus-visible': { outline: `2px solid ${theme.palette.primary[600]}`, outlineOffset: 2 },
                      cursor: 'pointer',
                      flex: 1,
                    }}
                    alignItems="center"
                    aria-pressed={isSelected}
                    data-testid={`comparison-plan-tab-${category}`}
                    role="button"
                    tabIndex={0}
                    onClick={selectCategory}
                    onKeyDown={(event) => onEnterOrSpace(event, selectCategory)}
                  >
                    <Stack
                      sx={{
                        borderBottom: '2px solid',
                        borderBottomColor: isSelected ? BRAND_BLUE : 'transparent',
                        paddingBottom: 4,
                        paddingTop: 4,
                        paddingX: 4,
                      }}
                      alignItems="center"
                    >
                      <Typography color="grey.900" variant="caption-1-md">
                        {CATEGORY_NAMES[category]}
                      </Typography>
                      <Typography color="grey.500" variant="caption-1-md">
                        {getPriceLabel(category)}
                      </Typography>
                    </Stack>
                  </Stack>
                );
              })}
            </Stack>

            <Button
              data-testid="comparison-get-started-button"
              sx={{ width: '100%' }}
              variant="primary"
              onClick={handleGetStarted}
            >
              {isEnterpriseSelected ? 'Contact us' : 'Get started'}
            </Button>
          </Stack>

          <Stack
            sx={{
              backgroundColor: 'primary.25',
              border: '1px solid',
              borderBottomLeftRadius: 16,
              borderBottomRightRadius: 16,
              borderColor: 'grey.200',
              borderTop: 'none',
              overflow: 'hidden',
            }}
          >
            {visibleRows.map((row, rowIndex) => {
              const values = getRowValues(row);
              return (
                <Stack
                  key={row.key}
                  sx={{
                    backgroundColor: rowIndex % 2 === 0 ? 'primary.25' : 'common.white',
                    borderBottom: '1px solid',
                    borderBottomColor: 'grey.200',
                    paddingX: 16,
                    paddingY: 18,
                  }}
                  alignItems="flex-start"
                  direction="row"
                  gap={12}
                  justifyContent="space-between"
                >
                  <Box sx={{ flexShrink: 0 }}>{renderLabel(row)}</Box>
                  <Box sx={{ flex: 1, minWidth: 0, textAlign: 'right' }}>
                    {renderValue(values[selectedCategoryIndex])}
                  </Box>
                </Stack>
              );
            })}
            {expandButton}
          </Stack>
        </Stack>
      </Stack>
    );
  }

  return (
    <Stack alignItems="center" gap={40} sx={{ backgroundColor: 'primary.50', paddingY: 60, width: '100%' }}>
      {heading}
      {/* No overflow on the wrapper so the header can stick to the scroll container's top. */}
      <Stack sx={{ maxWidth: 1260, width: '100%' }}>
        {/* header — split out of the overflow-hidden rows card, otherwise sticky is trapped */}
        <Stack
          sx={{
            backgroundColor: 'common.white',
            border: '1px solid',
            borderColor: 'grey.200',
            borderTopLeftRadius: 16,
            borderTopRightRadius: 16,
            minHeight: 96,
            paddingX: 20,
            position: 'sticky',
            top: 0,
            zIndex: 2,
          }}
          alignItems="center"
          direction="row"
        >
          <Stack gap={12} sx={{ flexShrink: 0, padding: 10, width: FEATURE_COLUMN_WIDTH }}>
            <Typography color="grey.900" variant="body-2-md">
              What changes
            </Typography>
            <Stack alignItems="center" direction="row" gap={8}>
              <Typography color="grey.500" variant="caption-1-md">
                Billed Annually
              </Typography>
              <Switch checked={yearlySelected} onChange={handleSwitch} />
            </Stack>
          </Stack>
          <Box sx={{ display: 'grid', flex: 1, gridTemplateColumns: 'repeat(4, minmax(0, 1fr))' }}>
            {CATEGORIES.map((category) => (
              <Stack key={category} gap={2} sx={{ padding: 10 }}>
                <Typography color="grey.900" variant="body-2-md">
                  {CATEGORY_NAMES[category]}
                </Typography>
                <Typography color="grey.500" variant="caption-1-md">
                  {getPriceLabel(category)}
                </Typography>
              </Stack>
            ))}
          </Box>
        </Stack>

        {/* rows — separate overflow-hidden card so zebra rows clip to the bottom radius */}
        <Stack
          sx={{
            backgroundColor: 'primary.25',
            border: '1px solid',
            borderBottomLeftRadius: 16,
            borderBottomRightRadius: 16,
            borderColor: 'grey.200',
            borderTop: 'none',
            overflow: 'hidden',
          }}
        >
          {visibleRows.map((row, rowIndex) => {
            const values = getRowValues(row);
            return (
              <Stack
                key={row.key}
                sx={{
                  backgroundColor: rowIndex % 2 === 0 ? 'primary.25' : 'common.white',
                  minHeight: 56,
                  paddingX: 20,
                }}
                alignItems="center"
                direction="row"
              >
                <Box sx={{ flexShrink: 0, padding: 10, width: FEATURE_COLUMN_WIDTH }}>{renderLabel(row)}</Box>
                <Box sx={{ display: 'grid', flex: 1, gridTemplateColumns: 'repeat(4, minmax(0, 1fr))' }}>
                  {values.map((value, index) => (
                    <Box key={CATEGORIES[index]} sx={{ padding: 10 }}>
                      {renderValue(value)}
                    </Box>
                  ))}
                </Box>
              </Stack>
            );
          })}

          {expandButton}
        </Stack>
      </Stack>
    </Stack>
  );
};
